import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useLang } from "../i18n";
import { useStore } from "../contexts/StoreContext";
import { createCheckout, formatPrice } from "../lib/api";
import { trackBeginCheckout, trackPurchaseIntent } from "../lib/analytics";
import { OBI } from "../assets/obi";

const COUNTRIES = [
  { code: "IT", name: "Italia" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Deutschland" },
  { code: "ES", name: "España" },
  { code: "CH", name: "Schweiz" },
  { code: "AT", name: "Österreich" },
];

const CheckoutPage = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { cart, subtotal, clearCart } = useStore();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sameAsBilling, setSameAsBilling] = useState(true);

  const [form, setForm] = useState({
    email: "",
    phone: "",
    billing: {
      first_name: "",
      last_name: "",
      address_1: "",
      address_2: "",
      city: "",
      state: "",
      postcode: "",
      country: "IT",
    },
    shipping: {
      first_name: "",
      last_name: "",
      address_1: "",
      address_2: "",
      city: "",
      state: "",
      postcode: "",
      country: "IT",
    },
    notes: "",
  });

  const updateBilling = (k, v) => setForm((f) => ({ ...f, billing: { ...f.billing, [k]: v } }));
  const updateShipping = (k, v) => setForm((f) => ({ ...f, shipping: { ...f.shipping, [k]: v } }));

  if (cart.length === 0 && !submitting) {
    return (
      <div className="max-w-3xl mx-auto p-12 text-center">
        <img src={OBI.shopping} alt="OBI" className="w-44 h-44 mx-auto" />
        <h2 className="font-display font-bold text-3xl mt-6">{t("cart.empty")}</h2>
        <p className="text-slate-600 mt-2">{t("cart.empty_sub")}</p>
        <Link to="/catalog" className="cartoon-btn bg-yellow-300 mt-6 inline-flex">
          {t("cart.continue_shopping")}
        </Link>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        items: cart.map((c) => ({ product_id: c.id, quantity: c.quantity })),
        customer: {
          email: form.email,
          phone: form.phone,
          billing: form.billing,
          shipping: sameAsBilling ? null : form.shipping,
        },
        notes: form.notes || "",
        payment_method: "ppcp-gateway",
        payment_method_title: "PayPal",
      };
      trackBeginCheckout(cart, subtotal);
      const res = await createCheckout(payload);
      // Fire purchase intent before leaving for the WooCommerce checkout URL.
      // The authoritative purchase event is fired server-side / on the
      // WooCommerce thank-you page (configurable later via Site Kit).
      trackPurchaseIntent(res.woo_order_id || res.local_order_id, cart, subtotal);
      // Clear local cart and redirect to WooCommerce checkout URL
      clearCart();
      window.location.href = res.checkout_url;
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail
        ? typeof err.response.data.detail === "string"
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail)
        : t("checkout.error");
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="bg-amber-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 font-bold text-slate-700 hover:text-pink-600 mb-6"
        >
          <ChevronLeft className="w-4 h-4" /> {t("checkout.back_to_cart")}
        </button>

        <h1 className="font-display font-bold text-4xl lg:text-5xl text-slate-900 mb-8">
          {t("checkout.title")}
        </h1>

        <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Contact */}
            <fieldset className="cartoon-card p-6">
              <legend className="font-display font-bold text-xl px-2">{t("checkout.contact")}</legend>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <input
                  data-testid="email-input"
                  type="email"
                  required
                  placeholder={t("checkout.email")}
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="cartoon-input w-full"
                />
                <input
                  data-testid="phone-input"
                  type="tel"
                  placeholder={t("checkout.phone")}
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="cartoon-input w-full"
                />
              </div>
            </fieldset>

            {/* Billing */}
            <fieldset className="cartoon-card p-6">
              <legend className="font-display font-bold text-xl px-2">{t("checkout.billing")}</legend>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <input required placeholder={t("checkout.first_name")} value={form.billing.first_name} onChange={(e) => updateBilling("first_name", e.target.value)} className="cartoon-input w-full" data-testid="billing-first-name" />
                <input required placeholder={t("checkout.last_name")} value={form.billing.last_name} onChange={(e) => updateBilling("last_name", e.target.value)} className="cartoon-input w-full" data-testid="billing-last-name" />
                <input required placeholder={t("checkout.address_1")} value={form.billing.address_1} onChange={(e) => updateBilling("address_1", e.target.value)} className="cartoon-input w-full sm:col-span-2" data-testid="billing-address-1" />
                <input placeholder={t("checkout.address_2")} value={form.billing.address_2} onChange={(e) => updateBilling("address_2", e.target.value)} className="cartoon-input w-full sm:col-span-2" />
                <input required placeholder={t("checkout.city")} value={form.billing.city} onChange={(e) => updateBilling("city", e.target.value)} className="cartoon-input w-full" data-testid="billing-city" />
                <input placeholder={t("checkout.state")} value={form.billing.state} onChange={(e) => updateBilling("state", e.target.value)} className="cartoon-input w-full" />
                <input required placeholder={t("checkout.postcode")} value={form.billing.postcode} onChange={(e) => updateBilling("postcode", e.target.value)} className="cartoon-input w-full" data-testid="billing-postcode" />
                <select required value={form.billing.country} onChange={(e) => updateBilling("country", e.target.value)} className="cartoon-input w-full">
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
            </fieldset>

            {/* Shipping */}
            <fieldset className="cartoon-card p-6">
              <legend className="font-display font-bold text-xl px-2">{t("checkout.shipping")}</legend>
              <label className="flex items-center gap-2 mt-4 font-bold cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsBilling}
                  onChange={(e) => setSameAsBilling(e.target.checked)}
                  className="w-5 h-5 accent-sky-500"
                  data-testid="same-as-billing"
                />
                {t("checkout.same_as_billing")}
              </label>
              {!sameAsBilling && (
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <input required placeholder={t("checkout.first_name")} value={form.shipping.first_name} onChange={(e) => updateShipping("first_name", e.target.value)} className="cartoon-input w-full" />
                  <input required placeholder={t("checkout.last_name")} value={form.shipping.last_name} onChange={(e) => updateShipping("last_name", e.target.value)} className="cartoon-input w-full" />
                  <input required placeholder={t("checkout.address_1")} value={form.shipping.address_1} onChange={(e) => updateShipping("address_1", e.target.value)} className="cartoon-input w-full sm:col-span-2" />
                  <input placeholder={t("checkout.address_2")} value={form.shipping.address_2} onChange={(e) => updateShipping("address_2", e.target.value)} className="cartoon-input w-full sm:col-span-2" />
                  <input required placeholder={t("checkout.city")} value={form.shipping.city} onChange={(e) => updateShipping("city", e.target.value)} className="cartoon-input w-full" />
                  <input placeholder={t("checkout.state")} value={form.shipping.state} onChange={(e) => updateShipping("state", e.target.value)} className="cartoon-input w-full" />
                  <input required placeholder={t("checkout.postcode")} value={form.shipping.postcode} onChange={(e) => updateShipping("postcode", e.target.value)} className="cartoon-input w-full" />
                  <select required value={form.shipping.country} onChange={(e) => updateShipping("country", e.target.value)} className="cartoon-input w-full">
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </fieldset>

            {/* Notes */}
            <fieldset className="cartoon-card p-6">
              <legend className="font-display font-bold text-xl px-2">{t("checkout.notes")}</legend>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="cartoon-input w-full mt-4"
              />
            </fieldset>

            {/* Payment */}
            <fieldset className="cartoon-card p-6 bg-sky-100">
              <legend className="font-display font-bold text-xl px-2">{t("checkout.payment")}</legend>
              <div className="flex items-center gap-3 mt-4">
                <span className="w-12 h-12 bg-yellow-300 border-4 border-slate-900 rounded-full flex items-center justify-center font-bold">P</span>
                <div>
                  <div className="font-bold">{t("checkout.payment_paypal")}</div>
                  <div className="text-sm text-slate-600">{t("checkout.payment_note")}</div>
                </div>
              </div>
            </fieldset>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-1">
            <div className="cartoon-card p-6 sticky top-28">
              <h3 className="font-display font-bold text-xl mb-4">{t("checkout.summary")}</h3>
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-3 text-sm">
                    <div className="w-14 h-14 bg-pink-100 border-2 border-slate-900 rounded-xl overflow-hidden flex-shrink-0">
                      {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold leading-tight line-clamp-2">{item.name}</p>
                      <p className="text-slate-500">{t("cart.qty")}: {item.quantity}</p>
                    </div>
                    <p className="font-bold">{formatPrice(item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t-2 border-dashed border-slate-300 my-4" />
              <div className="flex items-center justify-between mb-4">
                <span className="font-bold text-slate-700">{t("checkout.total")}</span>
                <span className="font-display font-bold text-2xl">{formatPrice(subtotal)}</span>
              </div>

              {error && (
                <div data-testid="checkout-error" className="bg-pink-200 border-4 border-pink-600 rounded-2xl p-3 mb-4 text-sm font-bold text-pink-800">
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="checkout-submit"
                disabled={submitting}
                className="cartoon-btn bg-pink-400 hover:bg-pink-300 w-full text-lg py-3 disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> {t("checkout.submitting")}</>
                ) : (
                  t("checkout.submit")
                )}
              </button>

              <div className="flex items-center gap-3 mt-6 bg-yellow-100 border-2 border-slate-900 rounded-2xl p-3">
                <img src={OBI.shopping} alt="OBI" className="w-14 h-14 object-contain" />
                <p className="text-xs text-slate-700 font-medium">OBI controlla l'ordine prima della spedizione!</p>
              </div>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
